import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { AuthTokens } from '../types';
import { cognitoHelpers } from './cognito';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  tokens: AuthTokens | null;
  login: () => Promise<void>;
  logout: () => void;
  setTokens: (tokens: AuthTokens) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokensState] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const storedTokens = cognitoHelpers.getTokens();
      if (storedTokens && !cognitoHelpers.isTokenExpired(storedTokens.idToken)) {
        setTokensState(storedTokens);
      } else if (storedTokens) {
        cognitoHelpers.clearTokens();
      }
    } catch {
      cognitoHelpers.clearTokens();
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = async () => {
    const url = await cognitoHelpers.getLoginUrl();
    window.location.href = url;
  };

  const logout = () => {
    cognitoHelpers.clearTokens();
    setTokensState(null);
    window.location.href = cognitoHelpers.getLogoutUrl();
  };

  const setTokens = (newTokens: AuthTokens) => {
    cognitoHelpers.saveTokens(newTokens);
    setTokensState(newTokens);
  };

  const value: AuthContextType = {
    isAuthenticated: tokens !== null && !cognitoHelpers.isTokenExpired(tokens.idToken),
    isLoading,
    tokens,
    login,
    logout,
    setTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
